<template>

  <v-dialog
    v-model="showDialog"
    @after-leave="onAfterLeave"
    scrollable
    :max-width="fullscreen ? undefined : 800"
    :fullscreen="fullscreen"
    :scrim="!fullscreen"
    :transition="fullscreen ? 'dialog-bottom-transition' : 'dialog-transition'"
  >

    <v-toolbar
      v-if="title !== undefined && title !== null"
      color="modal-title-background"
    >
      <v-toolbar-title :text="title" />
      <v-btn size="small" icon="$close" @click="showDialog = false" />
    </v-toolbar>

    <!-- the height of the main dialog content is constrained so the scrollable prop will work. -->
    <!-- this seems kinda dumb, but that's how it's shown in the Vuetify docs. -->
    <!-- https://vuetifyjs.com/en/components/dialogs/#scrollable -->
    <v-form @submit.prevent="submit" novalidate :style="{ maxHeight: fullscreen ? undefined : '60vh', height: fullscreen ? '100%' : undefined }" ref="modalForm">
      <v-card variant="elevated">

        <v-card-text>
          <slot></slot>
        </v-card-text>

        <v-card-actions>

          <slot name="actions"></slot>

          <v-spacer />

          <v-btn
            v-if="cancelButton"
            @click="showDialog = false"
            variant="tonal"
            :disabled="submitting"
          >{{ closeLabel ?? $t('actions.cancel') }}</v-btn>

          <v-btn
            v-if="submitButton"
            type="submit"
            :prepend-icon="submitIcon"
            :loading="submitting"
            :disabled="submitDisabled || submitting"
            variant="flat"
          >{{ submitLabel ?? $t('actions.save') }}</v-btn>

        </v-card-actions>

      </v-card>
    </v-form>

  </v-dialog>

</template>

<script setup lang="ts">

import { ref, watch, useTemplateRef } from 'vue'
// @ts-ignore
import { closeDialog } from 'vue3-promise-dialog'

interface Props {
  show?: boolean
  title?: string
  fullscreen?: boolean
  submit?: () => void
  submitting?: boolean
  submitButton?: boolean
  submitDisabled?: boolean
  submitLabel?: string
  submitIcon?: string
  cancelButton?: boolean
  closeLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  show: true,
  fullscreen: false,
  submit: () => null,
  submitting: false,
  cancelButton: true,
  submitButton: true,
  submitDisabled: false,
  submitIcon: '$confirm',
})

const modalForm = useTemplateRef('modalForm')
defineExpose({modalForm})

const showDialog = ref<boolean>(true)

watch(() => props.show, (newValue) => {
  if (!newValue) {
    // Trigger the modal to begin fading out
    showDialog.value = false
  }
})

// This function runs after the modal has finished fading out
function onAfterLeave() {
  closeDialog(false)
}

</script>
